// =============================================================================
// components/SplashScreen.tsx — JS splash shown during the 'loading' gate.
//
// Matches the client's reference: a periwinkle MESH gradient (soft light-blue +
// pink glow orbs low on the screen), a large ghosted tone-on-tone CRYB wordmark,
// a live % counter with a thin filling progress bar, and a "● cryb.ai" glass
// pill pinned near the bottom. The % / bar reflect a real load ramp (0→100),
// then onReady fires and SplashGate fades the cover to reveal the warm app.
// Native (SVG radial orbs + LinearGradient) — crisp text, no video asset.
// =============================================================================
import React, { useEffect, useRef, useState } from 'react';
import { StyleSheet, View, Text } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import LinearGradient from 'react-native-linear-gradient';
import Svg, { Defs, RadialGradient as SvgRadialGradient, Stop, Rect } from 'react-native-svg';
import { useSharedValue } from 'react-native-reanimated';
import SplashLogoFill from './SplashLogoFill';
import { spacing, radius, fonts, ThemeMode } from '../constants/theme';

export interface SplashScreenProps {
  /** Theme mode (kept for the caller's contract; the mark palette is fixed). */
  theme?: ThemeMode;
  /** Fired once the intro ramp settles (SplashGate then fades the cover). */
  onReady?: () => void;
}

// Periwinkle mesh palette — sampled pixel-for-pixel from the client reference.
const BG_TOP = '#8394FE'; // rgb(131,148,254) vivid periwinkle-blue
const BG_BOT = '#9098FC';

function Orb({ cx, cy, r, rgb, a, W, H }: { cx: number; cy: number; r: number; rgb: string; a: number; W: number; H: number }) {
  const id = `orb-${cx}-${cy}-${rgb.replace(/[^0-9]/g, '')}`;
  return (
    <>
      <Defs>
        <SvgRadialGradient id={id} cx={`${cx}%`} cy={`${cy}%`} r={`${r}%`}>
          <Stop offset="0%" stopColor={rgb} stopOpacity={a} />
          <Stop offset="60%" stopColor={rgb} stopOpacity={a * 0.35} />
          <Stop offset="100%" stopColor={rgb} stopOpacity={0} />
        </SvgRadialGradient>
      </Defs>
      <Rect x={0} y={0} width={W} height={H} fill={`url(#${id})`} />
    </>
  );
}

export default function SplashScreen({ onReady }: SplashScreenProps) {
  const insets = useSafeAreaInsets();
  const [pct, setPct] = useState(0);
  const [fadeIn, setFadeIn] = useState(0);
  const progress = useSharedValue(0);
  const done = useRef(false);

  useEffect(() => {
    // Content fade-in.
    const f = setTimeout(() => setFadeIn(1), 40);
    // Load ramp 0→100 (easeOutCubic) over ~1.9s, then onReady.
    const start = Date.now();
    const DUR = 4000;
    let raf: number;
    const tick = () => {
      const t = Math.min(1, (Date.now() - start) / DUR);
      const eased = 1 - Math.pow(1 - t, 3);
      progress.value = eased;
      setPct(Math.round(eased * 100));
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else if (!done.current) {
        done.current = true;
        onReady?.();
      }
    };
    raf = requestAnimationFrame(tick);
    return () => { clearTimeout(f); cancelAnimationFrame(raf); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <View style={styles.root}>
      {/* Base periwinkle gradient */}
      <LinearGradient colors={[BG_TOP, BG_BOT]} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} />
      {/* Mesh glow orbs — exact reference colors: lighter periwinkle upper-right,
          bright lavender lower-left, pink-purple lower-right, soft mid blend. */}
      <Svg style={StyleSheet.absoluteFill} pointerEvents="none">
        <Orb cx={84} cy={20} r={62} rgb="rgb(164,168,255)" a={0.5} W={9999} H={9999} />
        <Orb cx={15} cy={74} r={58} rgb="rgb(200,195,244)" a={0.72} W={9999} H={9999} />
        <Orb cx={85} cy={80} r={54} rgb="rgb(193,155,235)" a={0.62} W={9999} H={9999} />
        <Orb cx={50} cy={95} r={48} rgb="rgb(176,184,248)" a={0.45} W={9999} H={9999} />
      </Svg>

      {/* Center: CRYB letters filling with liquid + a subtle % readout */}
      <View style={[styles.center, { opacity: fadeIn }]} pointerEvents="none">
        <SplashLogoFill size={74} progress={progress} />
        <Text style={styles.pct}>{pct} %</Text>
      </View>

    </View>
  );
}

const WHITE = '#FFFFFF';
const styles = StyleSheet.create({
  root: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999, backgroundColor: BG_TOP },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  wordmark: {
    ...fonts.bold,
    fontSize: 76,
    letterSpacing: 4,
    color: 'rgba(255,255,255,0.24)', // ghosted tone-on-tone
    textAlign: 'center',
  },
  pct: {
    ...fonts.medium,
    fontSize: 13,
    letterSpacing: 1,
    color: 'rgba(255,255,255,0.85)',
    marginTop: spacing.lg,
    marginBottom: spacing.sm,
  },
  track: {
    width: '62%',
    height: 2.5,
    borderRadius: radius.full,
    backgroundColor: 'rgba(255,255,255,0.22)',
    overflow: 'hidden',
  },
  fill: { height: '100%', borderRadius: radius.full, backgroundColor: 'rgba(255,255,255,0.92)' },
  bottom: { position: 'absolute', left: 0, right: 0, bottom: 0, alignItems: 'center' },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: radius.full,
    backgroundColor: 'rgba(255,255,255,0.16)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.28)',
  },
  dot: { width: 6, height: 6, borderRadius: 3, backgroundColor: WHITE },
  pillText: { ...fonts.semibold, fontSize: 13, letterSpacing: 2, color: WHITE },
});
